import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { connect } from 'redux-bundler-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import _isEqual from 'lodash/isEqual';
import { Alert, Button } from '@trussworks/react-uswds';
import { mdiContentCopy } from '@mdi/js';
import { toast } from 'react-toastify';
import { tSuccess, tError } from '@common/toast/toastHelper';

import DataEntryTable from '@src/app-components/table/data-entry-table/DataEntryTable';
import Icon from '@src/app-components/icon/icon';
import { db } from '@src/app-pages/data-entry/offline/db';

import { FishDataEntrySchema, getBaseDefaultValues, getFishRiverDefaultValues } from './FishDataEntry.validation';
import { yesNoOptions } from '@src/app-pages/data-entry/edit-data-sheet/forms/_shared/selectHelper';
import { DataEntryStatuses, OfflineStatuses } from '@src/utils/enums';
import { createData, updateData } from '@src/app-pages/data-entry/offline/api';
import { getFishColumns } from './helpers.fish';
import { ensureTrailingBlankRow, isUntouchedPlaceholderRow } from '@src/app-pages/data-entry/dataEntryHelper';
import { getLookupOptions } from '@src/app-pages/data-entry/offline/lookup-cache';

import '@pages/data-summaries/data-summary.scss';
import '@pages/data-entry/dataentry.scss';

const lookupTableNames = ['fishCodes', 'fishStructures', 'floyTagPrefixes', 'lengthTypes', 'markRecaptureOptions'];
const noFishSpecies = ['NFSH', 'CNFH', 'CNA', 'NDNF'];
const gearAllowingTwoNfsh = ['LDN500', 'LDN750', 'LDN1000'];

const canDeleteUnsavedFishRow = (row) =>
  !isUntouchedPlaceholderRow(row) && !row?.fid && row?._status === OfflineStatuses.New;

const ensureFishTrailingBlankRow = (rows, gear) => {
  const normalizedRows = rows ?? [];
  const realRows = normalizedRows.filter((row) => !isUntouchedPlaceholderRow(row));
  const firstSpecies = realRows[0]?.species;
  const nfshCount = realRows.filter((row) => row?.species === 'NFSH').length;
  const canAddRow =
    !noFishSpecies.includes(firstSpecies) ||
    (firstSpecies === 'NFSH' && gearAllowingTwoNfsh.includes(gear) && nfshCount < 2);

  if (!canAddRow) {
    return realRows;
  }

  return ensureTrailingBlankRow(normalizedRows);
};

const normalizeFishRow = (row = {}) => ({
  ...row,
  fid: row?.fid ?? row?.fId ?? row?.f_id,
  fFid: row?.fFid ?? row?.f_fid,
  mrId: row?.mrId ?? row?.mr_id,
  mrFid: row?.mrFid ?? row?.mr_fid,
  panelHook: row?.panelHook ?? row?.panelhook ?? '',
  lengthType: row?.lengthType ?? row?.length_type ?? '',
  countF: row?.countF ?? row?.fishCount ?? '',
  ftPrefix: row?.ftPrefix ?? row?.ftprefix ?? '',
  floyTag: row?.floyTag ?? row?.ftnum ?? '',
  mR: row?.mR ?? row?.ftmr ?? '',
  geneticsVialNumber: row?.geneticsVialNumber ?? row?.genetics_vial_number ?? '',
  finCurl: row?.finCurl ?? row?.fin_curl ?? '',
  raySpine: row?.raySpine ?? row?.rayspine ?? '',
  KN: row?.KN ?? row?.kn ?? '',
  RSD: row?.RSD ?? row?.rsd ?? '',
  editInitials: row?.editInitials ?? row?.edit_initials ?? row?.edit_initial ?? '',
  uploadedBy: row?.uploadedBy ?? row?.uploaded_by ?? '',
});

// Calculate next Fish Field ID suffix from the current Fish table rows.
// Suffix always advances as max(existing suffix) + 1 for non-placeholder rows.
const getNextFishId = (data, parentMrId, parentMrFid) => {
  const existing = (data ?? []).filter((row) => !isUntouchedPlaceholderRow(row));

  let maxSequence = 0;

  existing.forEach((row) => {
    const id = row?.fFid ?? row?.f_fid ?? row?.localDisplayId ?? '';
    const sequencePart = String(id).split('-').pop();
    const sequenceNumber = Number(sequencePart);

    if (Number.isFinite(sequenceNumber) && sequenceNumber > maxSequence) {
      maxSequence = sequenceNumber;
    }
  });

  const sequenceText = String(maxSequence + 1).padStart(3, '0');

  return {
    fishFid: parentMrFid ? `${parentMrFid}-${sequenceText}` : undefined,
    localDisplayId: !parentMrFid && parentMrId ? `MR-${parentMrId}-${sequenceText}` : undefined,
  };
};

const FishDataEntry = connect(
  'doSaveFishDataEntry',
  'doUpdateFishDataEntry',
  'doSaveSupplementalDataEntry',
  'doUpdateSupplementalDataEntry',
  'doUpdateCurrentTab',
  'doMoRiverDatasheetLoadData',
  'selectDataEntryData',
  'selectDataEntryFishData',
  'selectDataEntrySupplemental',
  'selectBaseData',
  'selectLookupData',
  'selectRouteParams',
  ({
    doSaveFishDataEntry,
    doUpdateFishDataEntry,
    doSaveSupplementalDataEntry,
    doUpdateSupplementalDataEntry,
    doUpdateCurrentTab,
    doMoRiverDatasheetLoadData,
    dataEntryData,
    dataEntryFishData,
    dataEntrySupplemental,
    baseData,
    lookupData,
    onDirtyChange,
    routeParams,
  }) => {
    const { items } = dataEntryFishData;
    const siteRouteKey = routeParams?.siteId;
    const recoveryOutboxId = sessionStorage.getItem('syncRecoveryOutboxId');
    const { gear } = dataEntryData;

    // Default lookups to online data, otherwise will be overwritten by offline cached lookup data if network status = offline
    const [lookups, setLookups] = useState(
      lookupTableNames.reduce((accumulator, currentKey) => {
        accumulator[currentKey] = lookupData?.[currentKey] ?? [];
        return accumulator;
      }, {})
    );

    // Get Missouri River Draft Data
    const moriverDraftKey = `currentMissouriRiverDraft:${siteRouteKey}`;
    const savedDraft = sessionStorage.getItem(moriverDraftKey);
    const moriverDraft = savedDraft ? JSON.parse(savedDraft) : null;
    const parentMrFid = dataEntryData?.mrFid ?? dataEntryData?.mr_fid ?? moriverDraft?.mrFid ?? moriverDraft?.mr_fid;
    const parentMrId = dataEntryData?.mrId ?? dataEntryData?.mr_id ?? moriverDraft?.mrId ?? moriverDraft?.mr_id;
    const isOnline = navigator.onLine;

    const rowData = items?.map((item) => ({ ...normalizeFishRow(item), bendRiverMile: baseData?.bendRiverMile }));
    const [tableKey, setTableKey] = useState(0);
    const [data, setData] = useState(ensureFishTrailingBlankRow(rowData, gear));
    const [validationErrorRowCount, setValidationErrorRowCount] = useState(0);
    const [validationErrorRows, setValidationErrorRows] = useState([]);
    const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

    const handleRemoveMultipleRows = useCallback(
      (indicesToRemove) => {
        if (indicesToRemove.length !== 1) {
          window.alert('Remove fish rows one at a time.');
          return;
        }

        setData((oldData) => {
          const remainingRows = oldData.filter(
            (row, index) => !indicesToRemove.includes(index) || !canDeleteUnsavedFishRow(row)
          );
          return ensureFishTrailingBlankRow(remainingRows, gear);
        });
        setTableKey((old) => old + 1);
      },
      [gear]
    );

    const handleRemoveRow = useCallback((rowIndex) => handleRemoveMultipleRows([rowIndex]), [handleRemoveMultipleRows]);

    const firstFishSpecies = (data ?? []).find((row) => !isUntouchedPlaceholderRow(row))?.species;
    const isCopyLastRowDisabled = noFishSpecies.includes(firstFishSpecies);
    const panelHookRequired =
      (gear?.startsWith('TL') || gear?.startsWith('LDN')) && !noFishSpecies.includes(firstFishSpecies);
    const dataForValidation = (data ?? []).filter((row) => !isUntouchedPlaceholderRow(row));
    const schema = useMemo(() => FishDataEntrySchema({ gear, data: dataForValidation }), [gear, dataForValidation]);
    // Stable identity: prevents DataEntryTable's debounce effect from re-firing every render
    const tableValidationSchema = useMemo(
      () => ({
        validate: (row, options) => {
          if (isUntouchedPlaceholderRow(row)) {
            return Promise.resolve(row);
          }

          return schema.validate(row, options);
        },
      }),
      [schema]
    );

    const allSpeciesOptions = useMemo(
      () =>
        lookups?.fishCodes?.map((item) => ({
          code: item.alphaCode,
          description: item.commonName,
        })) ?? [],
      [lookups?.fishCodes]
    );

    // Memoized so identity is stable across renders where `data`/`gear` haven't changed
    const speciesOptions = useCallback(
      (tableRow) => {
        const realRows = (data ?? []).filter((row) => !isUntouchedPlaceholderRow(row));
        const firstSpecies = realRows[0]?.species;
        const isFirstRow = tableRow?.index === 0;
        const isOnlyFishRow = realRows.length <= 1;
        const isSecondRowNfsh = realRows[1]?.species === 'NFSH';
        const supportsTwoNfsh = gearAllowingTwoNfsh.includes(gear);
        const isFirstRowOfTwoNfsh = isFirstRow && firstSpecies === 'NFSH' && isSecondRowNfsh && supportsTwoNfsh;
        const canSelectNfsh =
          (isFirstRow && isOnlyFishRow) || (isFirstRow && isSecondRowNfsh && supportsTwoNfsh);
        const isRestrictedSecondNfshRow = tableRow?.index === 1 && firstSpecies === 'NFSH' && supportsTwoNfsh;

        return allSpeciesOptions.filter(({ code }) => {
          if (isFirstRowOfTwoNfsh) return code === 'NFSH';
          if (isRestrictedSecondNfshRow) return code === 'NFSH';
          if (code === 'NFSH') return canSelectNfsh;
          return !noFishSpecies.includes(code) || (isFirstRow && isOnlyFishRow);
        });
      },
      [data, gear, allSpeciesOptions]
    );

    const methods = useForm({
      resolver: yupResolver(schema),
      mode: 'onBlur',
    });

    const isFishCellRequired = useCallback(
      (row, columnId) => {
        if (!row || isUntouchedPlaceholderRow(row)) {
          return false;
        }

        const species = row?.species;
        const count = Number(row?.countF);
        const hasLength =
          row?.length !== null && row?.length !== undefined && row?.length !== '' && Number(row?.length) !== 0;

        const hasFloyTagPrefix = row?.ftPrefix != null && String(row.ftPrefix).trim() !== '';
        const hasFloyTag = row?.floyTag != null && String(row.floyTag).trim() !== '';

        if (columnId === 'length') {
          return ['PDSG', 'SNSG', 'SNPD'].includes(species) && count === 1;
        }

        if (columnId === 'lengthType') {
          const isRequiredBySpeciesAndCount = ['PDSG', 'SNSG', 'SNPD'].includes(species) && count === 1;
          return isRequiredBySpeciesAndCount || hasLength;
        }

        if (columnId === 'ftPrefix' || columnId === 'floyTag') {
          return hasFloyTagPrefix || hasFloyTag;
        }

        try {
          const description = schema.describe({ value: row });
          const tests = description?.fields?.[columnId]?.tests ?? [];

          return tests.some((test) => test?.name === 'required');
        } catch {
          return false;
        }
      },
      [schema]
    );

    // Stable identity: prevents forcing the table to re-measure/re-render all columns every render
    const tableColumns = useMemo(
      () =>
        getFishColumns({
          gear,
          speciesOptions,
          lengthTypes: lookups?.lengthTypes,
          floyTagPrefixes: lookups?.floyTagPrefixes,
          markRecaptureOptions: lookups?.markRecaptureOptions,
          yesNoOptions,
          fishStructures: lookups?.fishStructures,
          isOnline,
          panelHookRequired,
          canDeleteRow: canDeleteUnsavedFishRow,
          onRemoveRow: handleRemoveRow,
        }),
      [gear, speciesOptions, lookups, isOnline, panelHookRequired, handleRemoveRow]
    );

    const columnHeaderById = useMemo(() => {
      const headers = {};

      tableColumns.forEach((column) => {
        const key = column?.id ?? column?.accessorKey;
        if (!key) {
          return;
        }

        headers[String(key)] = typeof column?.header === 'string' ? column.header : String(key);
      });

      return headers;
    }, [tableColumns]);

    const scrollToBottom = useCallback(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      });
    }, []);

    const handleAddRow = async () => {
      setData((prev) => ensureFishTrailingBlankRow(prev, gear));
      scrollToBottom();
    };

    const handleCopyLastRowBtn = () => {
      if (!parentMrFid && !parentMrId) {
        console.error('Cannot copy Fish row: missing parent ID.');
        window.alert('Save the Missouri River draft first before copying Fish.');
        return;
      }
      const rows = (data ?? []).filter((row) => !isUntouchedPlaceholderRow(row));

      if (rows.length === 0) {
        window.alert('No existing Fish row found to copy.');
        return;
      }
      // Grab last object from data array
      const lastRowData = rows[rows.length - 1];
      const { fishFid, localDisplayId } = getNextFishId(rows, parentMrId, parentMrFid);
      const resolvedFishFid = fishFid ?? localDisplayId;

      // Format new row data
      const newRowData = {
        // ...lastRowData,
        fid: null, // Reset fid if copying a save data object
        clientId: crypto.randomUUID(),
        ...(parentMrId != null
          ? {
              mrId: Number(parentMrId),
              mr_id: Number(parentMrId),
            }
          : {}),
        ...(parentMrFid
          ? {
              mrFid: parentMrFid,
              mr_fid: parentMrFid,
            }
          : {}),
        ...(resolvedFishFid
          ? {
              fFid: resolvedFishFid,
              f_fid: resolvedFishFid,
            }
          : {}),
        ...(localDisplayId
          ? {
              localDisplayId,
            }
          : {}),
        species: lastRowData?.species,
        lengthType: lastRowData?.lengthType,
        countF: 1,
        _status: OfflineStatuses.New,
        _isPlaceholderRow: false,
        _isTouched: true,
      };
      setData((prev) => {
        const existingRows = (prev ?? []).filter((row) => !isUntouchedPlaceholderRow(row));
        return ensureFishTrailingBlankRow([...existingRows, newRowData], gear);
      });
      scrollToBottom();
    };

    const handleAddMultipleRows = (rows) => {
      // Handle any data mapping or formatting here
      setData((oldData) => {
        const existingRows = (oldData ?? []).filter((row) => !isUntouchedPlaceholderRow(row));
        return ensureFishTrailingBlankRow([...existingRows, ...rows], gear);
      });
      scrollToBottom();
    };

    const handleUpdateData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        const touchedPlaceholderRow = isUntouchedPlaceholderRow(data?.[rowIndex]);

        setData((oldData) => {
          const newData = oldData ? [...oldData] : [];
          if (newData[rowIndex]) {
            const currentRow = newData[rowIndex];
            const isPlaceholderRow = isUntouchedPlaceholderRow(currentRow);

            let nextRow = currentRow;
            if (isPlaceholderRow) {
              const { fishFid, localDisplayId } = getNextFishId(newData, parentMrId, parentMrFid);
              const resolvedFishFid = fishFid ?? localDisplayId;

              nextRow = {
                ...getBaseDefaultValues({ baseData }),
                ...getFishRiverDefaultValues({ dataEntryData }),
                clientId: crypto.randomUUID(),
                ...(parentMrId != null ? { mrId: Number(parentMrId), mr_id: Number(parentMrId) } : {}),
                ...(parentMrFid
                  ? {
                      mrFid: parentMrFid,
                      mr_fid: parentMrFid,
                    }
                  : {}),
                ...(resolvedFishFid
                  ? {
                      fFid: resolvedFishFid,
                      f_fid: resolvedFishFid,
                    }
                  : {}),
                ...(localDisplayId
                  ? {
                      localDisplayId,
                    }
                  : {}),
                _status: OfflineStatuses.New,
                _isPlaceholderRow: false,
                _isTouched: true,
              };
            }

            newData[rowIndex] = {
              ...nextRow,
              ...(columnId === null && typeof updatedValue === 'object' ? updatedValue : { [columnId]: updatedValue }),
              _isTouched: true,
            };

            if (!isPlaceholderRow && newData[rowIndex]._status !== OfflineStatuses.New) {
              newData[rowIndex]._status = OfflineStatuses.Edited;
            }

            return ensureFishTrailingBlankRow(newData, gear);
          }
          return oldData;
        });

        if (touchedPlaceholderRow) {
          scrollToBottom();
        }
      },
      [baseData, data, dataEntryData, gear, parentMrFid, parentMrId, scrollToBottom]
    );

    const handleSubmitAll = async () => {
      setIsSubmitAttempted(true);
      setValidationErrorRowCount(0);
      setValidationErrorRows([]);
      const toastId = toast.loading('Saving fish datasheet...');

      let nonPlaceholderRowNumber = 0;
      const rowsToProcess = (data ?? []).reduce((acc, row) => {
        if (isUntouchedPlaceholderRow(row)) {
          return acc;
        }

        nonPlaceholderRowNumber += 1;

        if (row._status === OfflineStatuses.New || row._status === OfflineStatuses.Edited) {
          acc.push({ item: row, rowNumber: nonPlaceholderRowNumber });
        }

        return acc;
      }, []);

      try {
        const rowPayloads =
          rowsToProcess?.map(({ item, rowNumber }) => {
            const isNew = !item.fid;
            const clientId = item.clientId ?? crypto.randomUUID();
            const parentRowMrId = item.mrId ?? item.mr_id;
            const parentRowMrFid = item.mrFid ?? item.mr_fid;
            const fishFid = item.fFid ?? item.f_fid ?? item.localDisplayId;

            const payload = {
              ...item,
              clientId: clientId,
              mr_id: parentRowMrId,
              mrId: parentRowMrId,
              mr_fid: parentRowMrFid,
              mrFid: parentRowMrFid,
              fFid: fishFid,
              f_fid: fishFid,
              _status: OfflineStatuses.Queued,
              version: item.version ?? 0,
              updatedAt: new Date().toISOString(),
              // Format values
              countF: item?.countF == null || item?.countF === '' ? null : Number(item?.countF),
              length: item?.['length'] == null || item?.['length'] === '' ? null : Number(item?.['length']),
              condition: item?.condition == null || item?.condition === '' ? null : Number(item?.condition),
              weight: item?.weight == null || item?.weight === '' ? null : Number(item?.weight),
            };

            return { item, payload, isNew, clientId, rowNumber };
          }) ?? [];

        // Validate all rows first; if any fail, stay on Fish and do not submit any rows.
        const validationResults = await Promise.all(
          rowPayloads.map(async ({ payload, rowNumber }) => {
            try {
              await schema.validate(payload, { abortEarly: false });
              return { isValid: true, rowNumber, errors: [] };
            } catch (error) {
              const validationErrors = error?.inner?.length ? error.inner : [error];
              const seen = new Set();
              const errors = validationErrors
                .map((item) => {
                  const message = item?.message;
                  if (!message) {
                    return null;
                  }

                  const columnId =
                    String(item?.path ?? '')
                      .split('.')
                      .pop() || '';
                  const columnName = (columnHeaderById[columnId] ?? columnId) || 'Row';
                  const key = `${columnName}|${message}`;
                  if (seen.has(key)) {
                    return null;
                  }

                  seen.add(key);
                  return { columnName, message };
                })
                .filter(Boolean);

              return {
                isValid: false,
                rowNumber,
                errors,
              };
            }
          })
        );
        const invalidRows = validationResults.filter((result) => !result.isValid);
        const invalidRowCount = invalidRows.length;

        if (invalidRowCount > 0) {
          toast.dismiss(toastId);
          setValidationErrorRowCount(invalidRowCount);
          setValidationErrorRows(invalidRows);
          scrollToBottom();
          return false;
        }

        const createGeneticsSupplemental = async (payload, fishSavedOnline) => {
          const geneticsVialNumber = payload?.geneticsVialNumber ?? payload?.genetics_vial_number;
          const supplementalCount = Number(payload?.supplementalData ?? 0);
          if (payload?.species !== 'USG' || !geneticsVialNumber) {
            return;
          }

          const fishId = payload?.fid ?? payload?.fId ?? payload?.f_id;
          const fishFid = payload?.fFid ?? payload?.f_fid;
          if (isOnline && fishSavedOnline && (!fishId || Number(fishId) <= 0)) {
            return;
          }

          const existingSupplemental = dataEntrySupplemental?.items?.find((row) => {
            const rowFishId = row?.fid ?? row?.fId ?? row?.f_id;
            const rowFishFid = row?.fFid ?? row?.f_fid ?? row?.ffid;
            return (
              (fishId != null && rowFishId != null && String(rowFishId) === String(fishId)) ||
              (fishFid && rowFishFid && String(rowFishFid) === String(fishFid))
            );
          });
          if (!existingSupplemental && supplementalCount > 0) {
            return;
          }

          const supplementalPayload = {
            ...existingSupplemental,
            fid: fishId,
            fId: fishId,
            f_id: fishId,
            fFid: fishFid,
            f_fid: fishFid,
            mrId: payload?.mrId ?? payload?.mr_id,
            mr_id: payload?.mr_id ?? payload?.mrId,
            genetic: 'Y',
            geneticsVialNumber,
            speciesId: 1,
            species_id: 1,
            sid: existingSupplemental?.sid ?? existingSupplemental?.sId ?? existingSupplemental?.s_id,
            clientId: existingSupplemental?.clientId ?? crypto.randomUUID(),
            _status: OfflineStatuses.Queued,
            version: existingSupplemental?.version ?? 0,
            updatedAt: new Date().toISOString(),
          };
          const supplementalFetchParams = {
            mrId: payload?.mrId ?? payload?.mr_id ?? parentMrId,
          };

          try {
            if (existingSupplemental && isOnline && fishSavedOnline) {
              const result = await doUpdateSupplementalDataEntry(supplementalPayload, supplementalFetchParams);
              if (result === false) throw new Error('Supplemental update failed.');
            } else if (existingSupplemental) {
              const clientId = existingSupplemental.clientId ?? supplementalPayload.clientId;
              if (!existingSupplemental.clientId) {
                await db.supplemental.put({ ...supplementalPayload, clientId });
              }
              await updateData('supplemental', clientId, supplementalPayload);
            } else if (isOnline && fishSavedOnline) {
              await doSaveSupplementalDataEntry(supplementalPayload, supplementalFetchParams);
            } else {
              await createData('supplemental', supplementalPayload);
            }
          } catch {
            if (existingSupplemental) {
              const clientId = existingSupplemental.clientId ?? supplementalPayload.clientId;
              if (!existingSupplemental.clientId) {
                await db.supplemental.put({ ...supplementalPayload, clientId });
              }
              await updateData('supplemental', clientId, supplementalPayload);
            } else {
              await createData('supplemental', supplementalPayload);
            }
          }

          payload.supplementalData = Math.max(supplementalCount, 1);
          if (payload.clientId) {
            await db.fish.update(payload.clientId, { supplementalData: payload.supplementalData });
          }
        };

        const supplementalFishRows = new Set();
        for (const { payload, isNew, clientId } of rowPayloads) {
          let fishSavedOnline = false;
          try {
            // Sync Recovery Logic
            if (payload?._syncRecoveryError && payload?.clientId) {
              if (isOnline) {
                const parentMrId = Number(payload?.mrId ?? payload?.mr_id ?? 0);
                if (!Number.isFinite(parentMrId) || parentMrId <= 0) {
                  throw new Error('Missouri River ID is missing.');
                }

                if (isNew) {
                  const result = await doSaveFishDataEntry(payload);
                  if (result === false) throw new Error('Fish save failed.');
                  const savedFishId = Number(result);
                  if (Number.isInteger(savedFishId) && savedFishId > 0) {
                    payload.fid = savedFishId;
                    payload.fId = savedFishId;
                    payload.f_id = savedFishId;
                  }
                } else {
                  const result = await doUpdateFishDataEntry(payload);
                  if (result === false) throw new Error('Fish save failed.');
                }
                fishSavedOnline = true;
                toast.dismiss(toastId);

                const recoveryItem = await db.outbox
                  .filter((item) => item.tableName === 'ds_fish' && String(item.clientId) === String(payload.clientId))
                  .first();

                if (recoveryItem?._id != null) {
                  await db.outbox.delete(recoveryItem._id);
                }

                const localRow = await db.fish.get(payload?.clientId);
                if (localRow) {
                  await db.fish.put({
                    ...localRow,
                    ...payload,
                    _status: DataEntryStatuses.Synced,
                    _syncRecoveryError: false,
                    _syncRecoveryMessage: undefined,
                  });
                }
                sessionStorage.removeItem('syncRecoveryOutboxId');
              } else {
                await updateData('fish', payload.clientId, payload);
              }
              await createGeneticsSupplemental(payload, fishSavedOnline);
              if (payload.supplementalData > 0) supplementalFishRows.add(payload.clientId);
              continue;
            }

            // Execute Submit
            if (isOnline) {
              const result = isNew ? await doSaveFishDataEntry(payload) : await doUpdateFishDataEntry(payload);
              if (result === false) throw new Error('Fish save failed.');
              if (isNew) {
                const savedFishId = Number(result);
                if (Number.isInteger(savedFishId) && savedFishId > 0) {
                  payload.fid = savedFishId;
                  payload.fId = savedFishId;
                  payload.f_id = savedFishId;
                }
              }
              fishSavedOnline = true;
            } else {
              isNew ? await createData('fish', payload) : await updateData('fish', clientId, payload);
            }
          } catch (error) {
            console.error('Fish save failed, queuing offline:', error);
            isNew ? await createData('fish', payload) : await updateData('fish', clientId, payload);
          }

          await createGeneticsSupplemental(payload, fishSavedOnline);
          if (payload.supplementalData > 0) supplementalFishRows.add(payload.clientId);
        }

        setData((prev) => {
          const existingRows = (prev ?? []).filter((row) => !isUntouchedPlaceholderRow(row));
          const updatedRows = existingRows.map((row) => {
            const matchingPayloadEntry = rowPayloads.find(({ item }) => item === row);

            if (!matchingPayloadEntry) return row;

            const recoveryRow = Boolean(matchingPayloadEntry.item?._syncRecoveryError);
            return {
              ...matchingPayloadEntry.payload,
              supplementalData: supplementalFishRows.has(matchingPayloadEntry.payload.clientId)
                ? Math.max(Number(matchingPayloadEntry.payload.supplementalData ?? 0), 1)
                : matchingPayloadEntry.payload.supplementalData,
              _status: recoveryRow && isOnline ? DataEntryStatuses.Synced : OfflineStatuses.Queued,
              _syncRecoveryError: recoveryRow && isOnline ? false : matchingPayloadEntry.payload?._syncRecoveryError,
              _syncRecoveryMessage:
                recoveryRow && isOnline ? undefined : matchingPayloadEntry.payload?._syncRecoveryMessage,
            };
          });
          return ensureFishTrailingBlankRow(updatedRows, gear);
        });

        tSuccess(toastId, 'Fish datasheet successfully updated.');

        const draft = savedDraft ? JSON.parse(savedDraft) : {};
        const fishCount = (data ?? []).filter((row) => !isUntouchedPlaceholderRow(row)).length;
        sessionStorage.setItem(moriverDraftKey, JSON.stringify({ ...draft, fishCount: fishCount }));
        const hasRecoveryRow = rowsToProcess.some(({ item }) => item?._syncRecoveryError && item?.clientId);
        if (!hasRecoveryRow) {
          await doMoRiverDatasheetLoadData(parentMrId ?? parentMrFid);
        }
        return true;
      } catch (err) {
        console.error('Submit failed:', err);
        tError(toastId, 'Error saving fish datasheet.');
        return false;
      }
    };

    const handleSaveAndClose = async () => {
      // Submit Data
      const isSaved = await handleSubmitAll();
      if (!isSaved) return;
      // Navigate to Missouri Entry Form Tab
      doUpdateCurrentTab(0);
    };

    useEffect(() => {
      if (recoveryOutboxId) return;
      const rowData = items?.map((item) => ({ ...normalizeFishRow(item), bendRiverMile: baseData?.bendRiverMile }));
      setData(ensureFishTrailingBlankRow(rowData, gear));
    }, [baseData?.bendRiverMile, gear, items, recoveryOutboxId]);

    useEffect(() => {
      const loadRecoveryRow = async () => {
        if (!recoveryOutboxId) return;
        const outboxItem = await db.outbox.get(Number(recoveryOutboxId));
        if (!outboxItem || outboxItem.tableName !== 'ds_fish') return;

        const localRow = await db.fish.get(outboxItem.clientId);
        const recoveryRow = localRow ?? outboxItem.payload;
        if (!recoveryRow) {
          console.warn('Unable to load Fish recovery row.', { outboxItem });
          return;
        }

        const recoveryMrKeys = [recoveryRow?.mrId, recoveryRow?.mr_id, recoveryRow?.mrFid, recoveryRow?.mr_fid]
          .filter((value) => value !== undefined && value !== null && value !== '')
          .map(String);

        const currentMrKeys = [parentMrId, parentMrFid]
          .filter((value) => value !== undefined && value !== null && value !== '')
          .map(String);
        const currentMoriver = recoveryMrKeys.some((key) => currentMrKeys.includes(key));
        if (!currentMoriver) {
          console.warn('Recovery Fish row does not match current Missouri River form.', {
            recoveryRow,
            parentMrId,
            parentMrFid,
          });
          return;
        }
        const formattedRecoveryRow = {
          ...recoveryRow,
          bendRiverMile: recoveryRow?.bendRiverMile ?? baseData?.bendRiverMile,
          _status: OfflineStatuses.Edited,
          _syncRecoveryError: Boolean(outboxItem.syncError),
          _syncRecoveryMessage: outboxItem.syncError ?? undefined,
          _isPlaceholderRow: false,
          _isTouched: true,
        };
        setData((currentRows) => {
          const rows = (currentRows ?? []).filter((row) => !isUntouchedPlaceholderRow(row));
          const index = rows.findIndex(
            (row) => row?.clientId && recoveryRow?.clientId && String(row.clientId) === String(recoveryRow.clientId)
          );
          if (index >= 0) {
            rows[index] = formattedRecoveryRow;
          } else {
            rows.push(formattedRecoveryRow);
          }
          return ensureFishTrailingBlankRow(rows, gear);
        });
      };
      void loadRecoveryRow();
    }, [recoveryOutboxId, parentMrId, parentMrFid, baseData?.bendRiverMile]);

    useEffect(() => {
      onDirtyChange?.(
        (data ?? []).some(
          (row) =>
            !isUntouchedPlaceholderRow(row) && [OfflineStatuses.New, OfflineStatuses.Edited].includes(row._status)
        )
      );
    }, [data, onDirtyChange]);

    // Load offline lookups
    useEffect(() => {
      const loadOfflineLookups = async () => {
        const entries = await Promise.all(lookupTableNames.map(async (name) => [name, await getLookupOptions(name)]));
        setLookups(Object.fromEntries(entries));
      };

      !isOnline && loadOfflineLookups();
    }, [isOnline]);

    return (
      <FormProvider {...methods}>
        <DataEntryTable
          addRow={handleAddRow}
          columns={tableColumns}
          data={data}
          enablePagination={false}
          enableRowSelection={false}
          initialTableState={{
            columnVisibility: {
              fid: false,
              otolith: false,
              raySpine: false,
              KN: false,
              RSD: false,
              editInitials: false,
              uploadedBy: false,
            },
            sorting: [{ id: 'fFid', desc: false }],
          }}
          key={tableKey}
          placeholderClick={handleAddRow}
          placeholderText='No Fish Data found.'
          removeMultipleRows={handleRemoveMultipleRows}
          addMultipleRows={handleAddMultipleRows}
          rowErrorCallback={() => {}}
          showAddRowButton={false}
          showValidationErrors={isSubmitAttempted}
          tableVersion='FishTable'
          updateSourceData={handleUpdateData}
          validationSchema={tableValidationSchema}
        />
        <Button
          className='margin-top-2 secondary-btn'
          disabled={isCopyLastRowDisabled}
          onClick={() => handleCopyLastRowBtn()}
          type='button'
        >
          <Icon focusable={false} className='margin-right-1' path={mdiContentCopy} />
          Copy Last Row
        </Button>
        <Button className='margin-top-2 add-btn' onClick={() => handleSubmitAll()} type='button'>
          Save
        </Button>
        <Button className='margin-top-2 add-btn' onClick={() => handleSaveAndClose()} type='button'>
          Save & Close Datasheet
        </Button>
        {validationErrorRowCount > 0 && (
          <Alert aria-live='polite' className='margin-y-1' headingLevel='h4' noIcon slim type='error'>
            <p className='margin-y-0'>
              {validationErrorRowCount} row{validationErrorRowCount === 1 ? '' : 's'}
              {validationErrorRowCount === 1 ? ' has ' : ' have '}validation errors that must be corrected before data
              can be submitted.
            </p>
            {validationErrorRows.length > 0 && (
              <ul className='margin-top-1 margin-bottom-0 padding-left-3'>
                {validationErrorRows.map((rowError) => (
                  <li key={`row-${rowError.rowNumber}`}>
                    Row {rowError.rowNumber}:{' '}
                    {rowError.errors?.length > 0
                      ? rowError.errors.map((errorItem, index) => (
                          <React.Fragment
                            key={`row-${rowError.rowNumber}-${errorItem.columnName}-${errorItem.message}-${index}`}
                          >
                            {index > 0 ? '; ' : ''}
                            <u>{errorItem.columnName}</u>: {errorItem.message}
                          </React.Fragment>
                        ))
                      : 'Validation error'}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </FormProvider>
    );
  }
);

export default FishDataEntry;
